import { mq } from "@/constants/breakpoints";
import {
  darkColor,
  iconColor,
  lightBlueColor,
  textColor,
} from "@/constants/colors";
import {
  signInUrl,
  termsPath,
  privacyPath,
  cookiesPath,
} from "@/constants/paths";
import { css } from "@emotion/react";
import Link from "next/link";
import HR from "../HR/HR";
import {
  Heading,
  Body,
  H8_16Css,
  H9Css,
  H7Css,
  H4Css,
  H6Css,
  BodyMedium16,
} from "../Typography";
import EmailPasswordForm from "./EmailPasswordForm";
import GoogleSignUpButton from "./GoogleSignUpButton";
import Balancer from "react-wrap-balancer";
import { useEffect, useState } from "react";
import Button from "../Buttons/Button";
import CrossIcon from "@/svg/CrossIcon";
import posthog from "posthog-js";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useCookies } from "react-cookie";

const PageLink = ({ small, withinDialog, ...props }: any) => (
  <Link
    target={withinDialog ? "_blank" : "_self"}
    css={css`
      color: ${lightBlueColor};
      font-weight: 350;
      transition: color 0.2s ease-in-out;
      font-size: ${small ? "14px" : "16px"};
      line-height: ${small ? "19.6px" : "22.4px"};
      &:hover {
        color: #45abe7;
      }
    `}
    {...props}
  />
);

const ReturnToHostnameCard = () => {
  const [cookies, , removeCookie] = useCookies([
    "jw_organizer_organization_uuid",
  ]);
  const [hostname, setHostname] = useState(undefined);
  const [isDismissed, setIsDismissed] = useState(false);
  const orgUUID = cookies["jw_organizer_organization_uuid"];

  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        const response = await fetch(
          `https://${process.env.appHostname}/api/organizations/${orgUUID}`,
          {
            headers: { Accept: "application/json" },
          }
        );
        if (response.ok) {
          const { hostname } = await response.json();
          setHostname(hostname);
          posthog.capture("RETURN_TO_SITE_CARD_OPENED");
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (orgUUID) fetchOrgData();
  }, [orgUUID]);

  const handleCloseButtonClick = () => {
    posthog.capture("RETURN_TO_SITE_CARD_DISMISSED");
    removeCookie("jw_organizer_organization_uuid");
    setHostname(undefined);
    setIsDismissed(true);
  };

  if (!hostname || isDismissed) return null;

  return (
    <div
      css={css`
        background-color: ${lightBlueColor};
        padding: 20px 30px;
        border-radius: 15px;
        width: 100%;
        color: #ffffff;
        position: relative;
        box-sizing: border-box;
        margin-bottom: 30px;
      `}
    >
      <Button
        type="button"
        jwType="lightBlue"
        onClick={handleCloseButtonClick}
        css={css`
          width: 24px;
          height: 24px;
          min-width: unset;
          border: none;
          padding: 0;
          border-radius: 5px;
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 1;
        `}
      >
        <CrossIcon
          css={css`
            color: ${iconColor};
          `}
        />
      </Button>

      <Heading
        level={4}
        css={css`
          color: #ffffff;
          font-size: 18px;
          font-weight: 400;
        `}
      >
        Return to your website?
      </Heading>

      <Body
        css={css`
          font-size: 12px;
          font-weight: 400;
          line-height: 150%;
          margin-top: 8px;
        `}
      >
        Are you trying to get back to{" "}
        <Link
          id="return-to-site-hostname"
          href={`https://${hostname}/admin/dashboard`}
          css={css`
            color: #ffffff;
            font-weight: 700;
            text-decoration: underline;
          `}
        >
          {hostname}
        </Link>
        ?
      </Body>
    </div>
  );
};

interface SignUpProps {
  primaryText?: string;
  secondaryText?: string;
  withinDialog?: boolean;
}

const SignUp = ({ primaryText, secondaryText, withinDialog }: SignUpProps) => {
  const featureFlagVariant = useFeatureFlagVariantKey("19-intro");

  return (
    <div
      css={css`
        max-width: 380px;
        margin: auto;
      `}
    >
      <ReturnToHostnameCard />
      <Heading
        level={1}
        css={css`
          color: ${darkColor};
          text-align: center;
          ${H7Css}
          ${mq["lg"]} {
            ${H6Css}
          }
          ${mq["xl"]} {
            ${H4Css}
            margin-left: -10px;
            margin-right: -10px;
          }
        `}
      >
        <Balancer>
          {primaryText || "Create your account for free to get started"}
        </Balancer>
      </Heading>
      {secondaryText && (
        <BodyMedium16
          css={css`
            color: ${textColor};
            text-align: center;
            margin-top: 8px;
          `}
        >
          {secondaryText}
        </BodyMedium16>
      )}
      <div
        css={css`
          margin-top: 24px;
          text-align: center;
        `}
      >
        <GoogleSignUpButton featureFlagVariant={featureFlagVariant} />
      </div>
      <div
        css={css`
          display: flex;
          color: ${textColor};
          text-align: center;
          align-items: center;
          justify-content: center;
          margin-top: 20px;
          gap: 10px;
          hr {
            width: 100%;
          }
        `}
      >
        <HR />
        <Body>Or</Body>
        <HR />
      </div>
      <EmailPasswordForm featureFlagVariant={featureFlagVariant} />
      <div
        css={css`
          margin-top: 22px;
        `}
      >
        <Heading
          css={css`
            color: ${textColor};
            text-align: center;
            ${H8_16Css}
          `}
        >
          If you already have an account,
          <br />
          <PageLink href={signInUrl}>click here to sign in</PageLink>.
        </Heading>
      </div>
      <div
        css={css`
          margin: 30px 0;
        `}
      >
        <HR />
      </div>
      <Body
        css={css`
          color: ${textColor};
          text-align: center;
          ${H9Css}
        `}
      >
        By signing up you agree to our{" "}
        <PageLink href={termsPath} withinDialog={withinDialog} small>
          Terms of Use
        </PageLink>
        , <br />
        <PageLink href={privacyPath} withinDialog={withinDialog} small>
          Privacy
        </PageLink>
        , and{" "}
        <PageLink href={cookiesPath} withinDialog={withinDialog} small>
          Cookie Policy
        </PageLink>
        .
      </Body>
    </div>
  );
};

export default SignUp;
